import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { getErrorMessage } from '../utils/errorMessages';

type TeamPerformanceRow = {
  userId: string;
  name: string;
  email: string;
  completedTasks: number;
  openTasks: number;
  overdueTasks: number;
};

type TeamPerformanceTeamTotals = {
  propertiesCreated: number;
  listingsLaunchedForSale: number;
  listingsLaunchedAuction: number;
  soldForSale: number;
  soldAuction: number;
  activeProperties: number;
};

type ConsultationsRow = {
  userId: string;
  name: string;
  email: string;
  consultationsCreated: number;
  consultationsCompleted: number;
  convertedToListing: number;
  consultationReportsGenerated: number;
  consultationReportsSent: number;
  saleTypeForSale: number;
  saleTypeAuction: number;
};

const getIsoWeekNumber = (date: Date) => {
  const temp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = temp.getUTCDay() || 7;
  temp.setUTCDate(temp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(temp.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((temp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return weekNo;
};

const formatTrendLabel = (label: string, granularity?: 'week' | 'month' | 'year') => {
  if (granularity === 'year') {
    if (/^\d{4}$/.test(label)) return label;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
    const [year, month, day] = label.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (granularity === 'week') {
      const end = new Date(date);
      end.setDate(end.getDate() + 6);
      const weekNo = getIsoWeekNumber(date);
      return `W${weekNo} (${date.toLocaleDateString('en-AU', {
        month: 'short',
        day: '2-digit',
      })}–${end.toLocaleDateString('en-AU', { month: 'short', day: '2-digit' })})`;
    }
    return date.toLocaleDateString('en-AU', { month: 'short', day: '2-digit' });
  }
  if (/^\d{4}-\d{2}$/.test(label)) {
    const [year, month] = label.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  }
  return label;
};

const linePath = (values: number[], width = 640, height = 160, pad = 16) => {
  if (values.length === 0) return '';
  const max = Math.max(1, ...values);
  const xStep = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0;
  return values
    .map((value, index) => {
      const x = pad + xStep * index;
      const y = height - pad - (value / max) * (height - pad * 2);
      return `${index === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');
};

const LineChart: React.FC<{
  values: number[];
  labels: string[];
  color: string;
  label?: string;
  showValues?: boolean;
  granularity?: 'week' | 'month' | 'year';
}> = ({ values, labels, color, label, showValues = true, granularity }) => {
  const width = 640;
  const chartHeight = 160;
  const axisLabelSpace = 28;
  const svgHeight = chartHeight + axisLabelSpace;
  const pad = 20;
  const path = linePath(values, width, chartHeight, pad);
  const max = Math.max(1, ...values);
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box sx={{ minWidth: width }}>
        <svg width={width} height={svgHeight} viewBox={`0 0 ${width} ${svgHeight}`}>
          {/* grid */}
          {[0, 0.5, 1].map((ratio) => {
            const y = pad + (1 - ratio) * (chartHeight - pad * 2);
            return (
              <line
                key={`grid-${ratio}`}
                x1={pad}
                x2={width - pad}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
            );
          })}
          <path d={path} fill="none" stroke={color} strokeWidth="3" />
          {values.map((value, index) => {
            const xStep = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0;
            const x = pad + xStep * index;
            const y = chartHeight - pad - (value / max) * (chartHeight - pad * 2);
            return (
              <g key={`${color}-${index}`}>
                <circle cx={x} cy={y} r="3" fill={color} />
                {showValues && (
                  <text
                    x={x}
                    y={y - 8}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#374151"
                  >
                    {value}
                  </text>
                )}
              </g>
            );
          })}
          {/* y-axis labels */}
          <text x={pad} y={pad - 6} fontSize="10" fill="#6b7280">
            {max}
          </text>
          <text x={pad} y={chartHeight - 4} fontSize="10" fill="#6b7280">
            0
          </text>
          {/* x-axis labels */}
          {labels.map((label, index) => {
            const xStep = labels.length > 1 ? (width - pad * 2) / (labels.length - 1) : 0;
            const baseX = pad + xStep * index;
            const isFirst = index === 0;
            const isLast = index === labels.length - 1;
            const textAnchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
            const labelOffset = 10;
            const x = isFirst ? baseX + labelOffset : isLast ? baseX - labelOffset : baseX;
            return (
              <text
                key={`x-${label}-${index}`}
                x={x}
                y={chartHeight + axisLabelSpace - 6}
                textAnchor={textAnchor}
                fontSize="9"
                fill="#6b7280"
              >
                {formatTrendLabel(label, granularity)}
              </text>
            );
          })}
        </svg>
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {label}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const BarChart: React.FC<{
  values: number[];
  labels: string[];
  color: string;
  label?: string;
  showValues?: boolean;
  granularity?: 'week' | 'month' | 'year';
}> = ({ values, labels, color, label, showValues = true, granularity }) => {
  const width = 640;
  const chartHeight = 160;
  const axisLabelSpace = 28;
  const svgHeight = chartHeight + axisLabelSpace;
  const pad = 20;
  const max = Math.max(1, ...values);
  const barCount = Math.max(values.length, 1);
  const band = (width - pad * 2) / barCount;
  const barWidth = Math.min(36, band * 0.7);
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box sx={{ minWidth: width }}>
        <svg width={width} height={svgHeight} viewBox={`0 0 ${width} ${svgHeight}`}>
          {/* grid */}
          {[0, 0.5, 1].map((ratio) => {
            const y = pad + (1 - ratio) * (chartHeight - pad * 2);
            return (
              <line
                key={`grid-${ratio}`}
                x1={pad}
                x2={width - pad}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
            );
          })}
          {values.map((value, index) => {
            const x = pad + band * index + (band - barWidth) / 2;
            const barHeight = (value / max) * (chartHeight - pad * 2);
            const y = chartHeight - pad - barHeight;
            return (
              <g key={`${color}-${index}`}>
                <rect x={x} y={y} width={barWidth} height={barHeight} fill={color} rx="3" />
                {showValues && (
                  <text
                    x={x + barWidth / 2}
                    y={y - 8}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#374151"
                  >
                    {value}
                  </text>
                )}
              </g>
            );
          })}
          {/* y-axis labels */}
          <text x={pad} y={pad - 6} fontSize="10" fill="#6b7280">
            {max}
          </text>
          <text x={pad} y={chartHeight - 4} fontSize="10" fill="#6b7280">
            0
          </text>
          {/* x-axis labels */}
          {labels.map((label, index) => {
            const x = pad + band * index + band / 2;
            return (
              <text
                key={`x-${label}-${index}`}
                x={x}
                y={chartHeight + axisLabelSpace - 6}
                textAnchor="middle"
                fontSize="9"
                fill="#6b7280"
              >
                {formatTrendLabel(label, granularity)}
              </text>
            );
          })}
        </svg>
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {label}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const Reports: React.FC = () => {
  const { agency, user } = useAuth();

  const toDateInput = (d: Date) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(() => toDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [end, setEnd] = useState(() => toDateInput(new Date()));
  const [saleTypeGroup, setSaleTypeGroup] = useState<'sale' | 'rental'>('sale');

  const applyRange = (range: 'this_month' | 'last_month' | 'this_year' | 'last_year' | 'ytd') => {
    const now = new Date();
    let rangeStart: Date;
    let rangeEnd: Date;

    switch (range) {
      case 'this_month': {
        rangeStart = new Date(now.getFullYear(), now.getMonth(), 1);
        rangeEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      }
      case 'last_month': {
        rangeStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        rangeEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      }
      case 'this_year': {
        rangeStart = new Date(now.getFullYear(), 0, 1);
        rangeEnd = new Date(now.getFullYear(), 11, 31);
        break;
      }
      case 'last_year': {
        rangeStart = new Date(now.getFullYear() - 1, 0, 1);
        rangeEnd = new Date(now.getFullYear() - 1, 11, 31);
        break;
      }
      case 'ytd':
      default: {
        rangeStart = new Date(now.getFullYear(), 0, 1);
        rangeEnd = now;
        break;
      }
    }

    setStart(toDateInput(rangeStart));
    setEnd(toDateInput(rangeEnd));
  };

  const shiftRange = (direction: 'prev' | 'next') => {
    const currentStart = new Date(start);
    const currentEnd = new Date(end);
    if (Number.isNaN(currentStart.getTime()) || Number.isNaN(currentEnd.getTime())) return;

    const multiplier = direction === 'prev' ? -1 : 1;

    const shiftBy = (startDate: Date, endDate: Date, unit: 'week' | 'month' | 'year') => {
      if (unit === 'week') {
        const delta = 7 * 24 * 60 * 60 * 1000 * multiplier;
        return {
          newStart: new Date(startDate.getTime() + delta),
          newEnd: new Date(endDate.getTime() + delta),
        };
      }
      if (unit === 'month') {
        const newStart = new Date(startDate.getFullYear(), startDate.getMonth() + multiplier, 1);
        const newEnd = new Date(newStart.getFullYear(), newStart.getMonth() + 1, 0);
        return { newStart, newEnd };
      }
      // year
      const newStart = new Date(startDate.getFullYear() + multiplier, 0, 1);
      const newEnd = new Date(newStart.getFullYear(), 11, 31);
      return { newStart, newEnd };
    };

    const diffDays = Math.abs(currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24);
    const shiftUnit: 'week' | 'month' | 'year' =
      diffDays >= 320 ? 'year' : diffDays >= 28 ? 'month' : 'week';

    const { newStart, newEnd } = shiftBy(currentStart, currentEnd, shiftUnit);
    setStart(toDateInput(newStart));
    setEnd(toDateInput(newEnd));
  };

  const [perfRows, setPerfRows] = useState<TeamPerformanceRow[]>([]);
  const [perfTeam, setPerfTeam] = useState<TeamPerformanceTeamTotals | null>(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfError, setPerfError] = useState<string | null>(null);

  const [consultRows, setConsultRows] = useState<ConsultationsRow[]>([]);
  const [consultLoading, setConsultLoading] = useState(false);
  const [consultError, setConsultError] = useState<string | null>(null);

  const [trendGranularity, setTrendGranularity] = useState<'week' | 'month' | 'year'>('month');
  const [chartStyle, setChartStyle] = useState<'bar' | 'line'>('bar');
  const [trendData, setTrendData] = useState<any>(null);

  const canFetch = useMemo(() => Boolean(agency?.slug), [agency?.slug]);

  const isSingleYearRange = useMemo(() => {
    const rangeStart = new Date(start);
    const rangeEnd = new Date(end);
    if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime())) return false;
    const sameYear = rangeStart.getFullYear() === rangeEnd.getFullYear();
    const isYearBounds =
      rangeStart.getMonth() === 0 &&
      rangeStart.getDate() === 1 &&
      rangeEnd.getMonth() === 11 &&
      rangeEnd.getDate() === 31;
    return sameYear && isYearBounds;
  }, [start, end]);

  useEffect(() => {
    if (!isSingleYearRange) return;
    if (trendGranularity === 'year') {
      setTrendGranularity('month');
    }
  }, [isSingleYearRange, trendGranularity]);

  const fetchAll = async () => {
    if (!agency?.slug) return;

    setPerfLoading(true);
    setConsultLoading(true);
    setPerfError(null);
    setConsultError(null);

    try {
      const [perfResp, consultResp, trendResp] = await Promise.all([
        api.get(`/agencies/${agency.slug}/reports/team-performance`, {
          params: { start, end, saleTypeGroup },
        }),
        api.get(`/agencies/${agency.slug}/reports/consultations`, {
          params: { start, end, saleTypeGroup },
        }),
        api.get(`/agencies/${agency.slug}/reports/trends`, {
          params: { start, end, granularity: trendGranularity, saleTypeGroup },
        }),
      ]);
      setPerfRows(perfResp.data?.data || []);
      setPerfTeam(perfResp.data?.team || null);
      setConsultRows(consultResp.data?.data || []);
      setTrendData(trendResp.data || null);
    } catch (err: any) {
      const message = getErrorMessage(err);
      // Keep it simple: if anything fails, show a generic message + keep per-table errors empty.
      setPerfError(message);
      setConsultError(message);
    } finally {
      setPerfLoading(false);
      setConsultLoading(false);
    }
  };

  useEffect(() => {
    if (!canFetch) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch]);

  useEffect(() => {
    if (!canFetch) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, canFetch, trendGranularity, saleTypeGroup]);

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const Chart = chartStyle === 'bar' ? BarChart : LineChart;

  const TabPanel: React.FC<{ value: number; index: number; children: React.ReactNode }> = ({
    value,
    index,
    children,
  }) => {
    if (value !== index) return null;
    return <Box sx={{ mt: 3 }}>{children}</Box>;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Reports
          </Typography>
          <Button
            variant="contained"
            onClick={fetchAll}
            disabled={!canFetch || perfLoading || consultLoading}
          >
            Refresh
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={() => applyRange('this_month')}>
              This month
            </Button>
            <Button variant="outlined" size="small" onClick={() => applyRange('last_month')}>
              Last month
            </Button>
            <Button variant="outlined" size="small" onClick={() => applyRange('ytd')}>
              YTD
            </Button>
            <Button variant="outlined" size="small" onClick={() => applyRange('this_year')}>
              This year
            </Button>
            <Button variant="outlined" size="small" onClick={() => applyRange('last_year')}>
              Last year
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              type="date"
              size="small"
              label="Start"
              InputLabelProps={{ shrink: true }}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
            <TextField
              type="date"
              size="small"
              label="End"
              InputLabelProps={{ shrink: true }}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
            <Button
              size="small"
              variant={saleTypeGroup === 'sale' ? 'contained' : 'outlined'}
              onClick={() => setSaleTypeGroup('sale')}
            >
              Sales
            </Button>
            <Button
              size="small"
              variant={saleTypeGroup === 'rental' ? 'contained' : 'outlined'}
              onClick={() => setSaleTypeGroup('rental')}
            >
              Rentals
            </Button>
          </Box>
        </Box>
      </Paper>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Listings / Sales (Team)" />
        <Tab label="Initial Consultations" />
        <Tab label="Tasks Performance" />
      </Tabs>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <IconButton aria-label="Previous period" onClick={() => shiftRange('prev')} size="small">
            <ChevronLeft />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Trend
            </Typography>
            <Button
              size="small"
              variant={trendGranularity === 'week' ? 'contained' : 'outlined'}
              onClick={() => setTrendGranularity('week')}
            >
              Weekly
            </Button>
            <Button
              size="small"
              variant={trendGranularity === 'month' ? 'contained' : 'outlined'}
              onClick={() => setTrendGranularity('month')}
            >
              Monthly
            </Button>
            <Button
              size="small"
              variant={trendGranularity === 'year' ? 'contained' : 'outlined'}
              onClick={() => setTrendGranularity('year')}
              disabled={isSingleYearRange}
            >
              Yearly
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Button
              size="small"
              variant={chartStyle === 'bar' ? 'contained' : 'outlined'}
              onClick={() => setChartStyle('bar')}
            >
              Bars
            </Button>
            <Button
              size="small"
              variant={chartStyle === 'line' ? 'contained' : 'outlined'}
              onClick={() => setChartStyle('line')}
            >
              Lines
            </Button>
            {isSingleYearRange && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Year range shows monthly/weekly trends.
              </Typography>
            )}
          </Box>

          <IconButton aria-label="Next period" onClick={() => shiftRange('next')} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
      </Paper>

      {/* Tasks Performance (per person) */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Tasks Performance
        </Typography>

        {perfError && <Alert severity="error" sx={{ mb: 2 }}>{perfError}</Alert>}
        {perfLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {trendData?.tasks?.length ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Trend: tasks completed ({trendGranularity})
                </Typography>
                <Chart
                  values={trendData.tasks.map((row: any) => row.completed)}
                  labels={trendData.tasks.map((row: any) => row.period)}
                  color="#2563eb"
                  label="Tasks completed"
                  granularity={trendGranularity}
                />
              </Box>
            ) : null}
            {perfRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Task activity chart
                </Typography>
                {(() => {
                  const maxTaskValue = Math.max(
                    1,
                    ...perfRows.flatMap((r) => [r.completedTasks, r.openTasks, r.overdueTasks])
                  );
                  return perfRows.map((r) => (
                    <Box key={`task-chart-${r.userId}`} sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Completed: {r.completedTasks}
                          </Typography>
                          <Box sx={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${(r.completedTasks / maxTaskValue) * 100}%`,
                                backgroundColor: '#22c55e',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Open: {r.openTasks}
                          </Typography>
                          <Box sx={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${(r.openTasks / maxTaskValue) * 100}%`,
                                backgroundColor: '#3b82f6',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Overdue: {r.overdueTasks}
                          </Typography>
                          <Box sx={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${(r.overdueTasks / maxTaskValue) * 100}%`,
                                backgroundColor: '#f59e0b',
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="right">Tasks completed</TableCell>
                    <TableCell align="right">Open tasks</TableCell>
                    <TableCell align="right">Overdue tasks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {perfRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No performance data found for this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    perfRows.map((r) => (
                      <TableRow key={r.userId}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{r.completedTasks}</TableCell>
                        <TableCell align="right">{r.openTasks}</TableCell>
                        <TableCell align="right">
                          {r.overdueTasks > 0 ? <Chip size="small" color="warning" label={r.overdueTasks} /> : 0}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TabPanel>

      {/* Listings / Sales (team totals) */}
      <TabPanel value={activeTab} index={0}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Listings / Sales (Team)
        </Typography>

        {!perfLoading && !perfError && !perfTeam && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No team totals available for this range.
          </Alert>
        )}

        {perfLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : perfError ? null : (
          <>
            {trendData?.listings?.launched?.length ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Trend: listings launched ({trendGranularity})
                </Typography>
                {(() => {
                  const periods = Array.from(
                    new Set(trendData.listings.launched.map((row: any) => row.period))
                  );
                  const forSale = periods.map((period: string) =>
                    trendData.listings.launched
                      .filter((row: any) => row.period === period && row.saleType === 'for sale')
                      .reduce((sum: number, row: any) => sum + row.count, 0)
                  );
                  const auction = periods.map((period: string) =>
                    trendData.listings.launched
                      .filter((row: any) => row.period === period && row.saleType === 'auction')
                      .reduce((sum: number, row: any) => sum + row.count, 0)
                  );
                  return (
                    <>
                      <Chart values={forSale} labels={periods} color="#2563eb" label="For sale" granularity={trendGranularity} />
                      <Box sx={{ mt: 1 }}>
                        <Chart values={auction} labels={periods} color="#9333ea" label="Auction" granularity={trendGranularity} />
                      </Box>
                    </>
                  );
                })()}
              </Box>
            ) : null}
            {trendData?.listings?.sold?.length ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Trend: sold ({trendGranularity})
                </Typography>
                {(() => {
                  const periods = Array.from(
                    new Set(trendData.listings.sold.map((row: any) => row.period))
                  );
                  const forSale = periods.map((period: string) =>
                    trendData.listings.sold
                      .filter((row: any) => row.period === period && row.saleType === 'for sale')
                      .reduce((sum: number, row: any) => sum + row.count, 0)
                  );
                  const auction = periods.map((period: string) =>
                    trendData.listings.sold
                      .filter((row: any) => row.period === period && row.saleType === 'auction')
                      .reduce((sum: number, row: any) => sum + row.count, 0)
                  );
                  return (
                    <>
                      <Chart values={forSale} labels={periods} color="#22c55e" label="For sale" granularity={trendGranularity} />
                      <Box sx={{ mt: 1 }}>
                        <Chart values={auction} labels={periods} color="#f97316" label="Auction" granularity={trendGranularity} />
                      </Box>
                    </>
                  );
                })()}
              </Box>
            ) : null}
            {perfTeam && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Listings / sales mix
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Listings launched (For sale vs Auction)
                  </Typography>
                  <Box sx={{ height: 10, backgroundColor: '#e5e7eb', borderRadius: 5, overflow: 'hidden' }}>
                    {(() => {
                      const total = (perfTeam.listingsLaunchedForSale || 0) + (perfTeam.listingsLaunchedAuction || 0) || 1;
                      return (
                        <Box sx={{ display: 'flex', height: '100%' }}>
                          <Box sx={{ width: `${(perfTeam.listingsLaunchedForSale / total) * 100}%`, backgroundColor: '#2563eb' }} />
                          <Box sx={{ width: `${(perfTeam.listingsLaunchedAuction / total) * 100}%`, backgroundColor: '#9333ea' }} />
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Sold (For sale vs Auction)
                  </Typography>
                  <Box sx={{ height: 10, backgroundColor: '#e5e7eb', borderRadius: 5, overflow: 'hidden' }}>
                    {(() => {
                      const total = (perfTeam.soldForSale || 0) + (perfTeam.soldAuction || 0) || 1;
                      return (
                        <Box sx={{ display: 'flex', height: '100%' }}>
                          <Box sx={{ width: `${(perfTeam.soldForSale / total) * 100}%`, backgroundColor: '#22c55e' }} />
                          <Box sx={{ width: `${(perfTeam.soldAuction / total) * 100}%`, backgroundColor: '#f97316' }} />
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell align="right">For sale</TableCell>
                    <TableCell align="right">Auction</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Listings launched</TableCell>
                    <TableCell align="right">{perfTeam?.listingsLaunchedForSale ?? 0}</TableCell>
                    <TableCell align="right">{perfTeam?.listingsLaunchedAuction ?? 0}</TableCell>
                    <TableCell align="right">
                      {(perfTeam?.listingsLaunchedForSale ?? 0) + (perfTeam?.listingsLaunchedAuction ?? 0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Sold</TableCell>
                    <TableCell align="right">{perfTeam?.soldForSale ?? 0}</TableCell>
                    <TableCell align="right">{perfTeam?.soldAuction ?? 0}</TableCell>
                    <TableCell align="right">
                      {(perfTeam?.soldForSale ?? 0) + (perfTeam?.soldAuction ?? 0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Active properties</TableCell>
                    <TableCell align="right" colSpan={3}>
                      {perfTeam?.activeProperties ?? 0}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TabPanel>

      {/* Initial Consultations */}
      <TabPanel value={activeTab} index={1}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Initial Consultations
        </Typography>

        {consultError && <Alert severity="error" sx={{ mb: 2 }}>{consultError}</Alert>}
        {consultLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {trendData?.consultations?.created?.length ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Trend: consultations ({trendGranularity})
                </Typography>
                {(() => {
                  const periods = Array.from(
                    new Set(trendData.consultations.created.map((row: any) => row.period))
                  );
                  const created = periods.map((period: string) =>
                    trendData.consultations.created.find((r: any) => r.period === period)?.created ?? 0
                  );
                  const completed = periods.map((period: string) =>
                    trendData.consultations.completed.find((r: any) => r.period === period)?.completed ?? 0
                  );
                  const converted = periods.map((period: string) =>
                    trendData.consultations.converted.find((r: any) => r.period === period)?.converted ?? 0
                  );
                  return (
                    <>
                      <Chart values={created} labels={periods} color="#6366f1" label="Created" granularity={trendGranularity} />
                      <Box sx={{ mt: 1 }}>
                        <Chart values={completed} labels={periods} color="#10b981" label="Completed" granularity={trendGranularity} />
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <Chart values={converted} labels={periods} color="#f97316" label="Converted" granularity={trendGranularity} />
                      </Box>
                    </>
                  );
                })()}
              </Box>
            ) : null}
            {consultRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Consultation volume
                </Typography>
                {(() => {
                  const maxConsultValue = Math.max(
                    1,
                    ...consultRows.flatMap((r) => [
                      r.consultationsCreated,
                      r.consultationsCompleted,
                      r.convertedToListing
                    ])
                  );
                  return consultRows.map((r) => (
                    <Box key={`consult-chart-${r.userId}`} sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Created: {r.consultationsCreated}
                          </Typography>
                          <Box sx={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${(r.consultationsCreated / maxConsultValue) * 100}%`,
                                backgroundColor: '#6366f1',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Completed: {r.consultationsCompleted}
                          </Typography>
                          <Box sx={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${(r.consultationsCompleted / maxConsultValue) * 100}%`,
                                backgroundColor: '#10b981',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Converted: {r.convertedToListing}
                          </Typography>
                          <Box sx={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${(r.convertedToListing / maxConsultValue) * 100}%`,
                                backgroundColor: '#f97316',
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="right">Consultations created</TableCell>
                    <TableCell align="right">Consultations completed</TableCell>
                    <TableCell align="right">Converted to listing</TableCell>
                    <TableCell align="right">Reports generated</TableCell>
                    <TableCell align="right">Reports sent</TableCell>
                    <TableCell align="right">Sale type (For sale)</TableCell>
                    <TableCell align="right">Sale type (Auction)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consultRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No consultation data found for this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    consultRows.map((r) => (
                      <TableRow key={r.userId}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{r.consultationsCreated}</TableCell>
                        <TableCell align="right">{r.consultationsCompleted}</TableCell>
                        <TableCell align="right">{r.convertedToListing}</TableCell>
                        <TableCell align="right">{r.consultationReportsGenerated}</TableCell>
                        <TableCell align="right">{r.consultationReportsSent}</TableCell>
                        <TableCell align="right">{r.saleTypeForSale}</TableCell>
                        <TableCell align="right">{r.saleTypeAuction}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TabPanel>
    </Container>
  );
};

export default Reports;

